import { axiosInstance, axiosReportingInstance } from './axiosInstance';
import { AxiosResponse, AxiosError, InternalAxiosRequestConfig, AxiosInstance } from 'axios';
import { clientConfig } from './config';
import { isTokenExpired } from 'src/utils/helpers';

interface FailedRequest {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}

const replaceToken = async () => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }
  const response = await axiosInstance.post<{ token: string; refreshToken: string }>('/token', { refreshToken });
  localStorage.setItem('token', response.data.token);
  localStorage.setItem('refreshToken', response.data.refreshToken);
  return response.data.token;
};

let isRefreshing = false;
let failedQueue: FailedRequest[] = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const handleTokenRequestError = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  window.location.href = '/login';
};

const requiresAuth = (config: InternalAxiosRequestConfig): boolean => {
  const publicEndpoints = ['/token', '/login'];
  return !publicEndpoints.some((endpoint) => config.url?.includes(endpoint));
};

const trimHeader = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

const setDefaultClientHeaderIfMissing = (config: InternalAxiosRequestConfig) => {
  config.headers = config.headers || {};
  const trimmedHeader = trimHeader(config.headers.get('Client'));
  if (!trimmedHeader) {
    config.headers.set('client', clientConfig.getClient());
  }
};

axiosReportingInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    const token = localStorage.getItem('token');
    setDefaultClientHeaderIfMissing(config);

    if (requiresAuth(config) && token) {
      if (isTokenExpired(token)) {
        if (isRefreshing) {
          try {
            const newToken = await new Promise<string>((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            });
            config.headers.set('Authorization', `Bearer ${newToken}`);
          } catch (err) {
            handleTokenRequestError();
            throw err;
          }
        } else {
          isRefreshing = true;
          try {
            const newToken = await replaceToken();
            config.headers.set('Authorization', `Bearer ${newToken}`);
            processQueue(null, newToken);
          } catch (err) {
            processQueue(err, null);
            handleTokenRequestError();
            throw err;
          } finally {
            isRefreshing = false;
          }
        }
      } else {
        config.headers.set('Authorization', `Bearer ${token}`);
      }
    }

    return config;
  },
  (error: AxiosError): Promise<AxiosError> => Promise.reject(error),
);

axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    const token = localStorage.getItem('token');
    setDefaultClientHeaderIfMissing(config);

    if (requiresAuth(config) && token) {
      if (isTokenExpired(token)) {
        if (isRefreshing) {
          try {
            const newToken = await new Promise<string>((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            });
            config.headers.set('Authorization', `Bearer ${newToken}`);
          } catch (err) {
            handleTokenRequestError();
            throw err;
          }
        } else {
          isRefreshing = true;
          try {
            const newToken = await replaceToken();
            config.headers.set('Authorization', `Bearer ${newToken}`);
            processQueue(null, newToken);
          } catch (err) {
            processQueue(err, null);
            handleTokenRequestError();
            throw err;
          } finally {
            isRefreshing = false;
          }
        }
      } else {
        config.headers.set('Authorization', `Bearer ${token}`);
      }
    }

    return config;
  },
  (error: AxiosError): Promise<AxiosError> => Promise.reject(error),
);

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

const refreshAuthToken = async (): Promise<string> => {
  if (isRefreshing) {
    return new Promise<string>((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }
  isRefreshing = true;
  try {
    const newToken = await replaceToken();
    processQueue(null, newToken);
    return newToken;
  } catch (err) {
    processQueue(err, null);
    throw err;
  } finally {
    isRefreshing = false;
  }
};

const createResponseErrorHandler =
  (instance: AxiosInstance) =>
  async (error: AxiosError): Promise<unknown> => {
    const originalConfig = error.config as RetriableConfig | undefined;
    const status = error.response?.status;

    if ((status === 401 || status === 403) && originalConfig?.url?.includes('/token')) {
      handleTokenRequestError();
      return Promise.reject(error);
    }

    if (status === 401 && originalConfig && requiresAuth(originalConfig) && !originalConfig._retry) {
      if (!localStorage.getItem('refreshToken')) {
        handleTokenRequestError();
        return Promise.reject(error);
      }
      originalConfig._retry = true;
      try {
        const newToken = await refreshAuthToken();
        originalConfig.headers.set('Authorization', `Bearer ${newToken}`);
        return instance(originalConfig);
      } catch (refreshError) {
        handleTokenRequestError();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  };

axiosInstance.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => response,
  createResponseErrorHandler(axiosInstance),
);

axiosReportingInstance.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => response,
  createResponseErrorHandler(axiosReportingInstance),
);

export { axiosInstance, axiosReportingInstance };
